// React Query
import { useMutation, useQuery } from "@tanstack/react-query";
// MUI
import { Stack, Typography } from "@mui/material";

// Third Party
import { get, set } from "lodash-es";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId.tsx";

// API
import { updateAdministrationTools } from "@Settings/api/AdministrationTools/mutations.ts";
import { getProviderList, getSystemTenant } from "@Settings/api/AdministrationTools/queries.ts";

// Helpers
import { and, or } from "@Common/helpers/sonar.tsx";

// Components
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";
import SimpleTextField from "@Layouts/components/InputComponents/SimpleTextField";

// Types
import { IAdministrationTools } from "@Settings/types/administrationTools/model.ts";
import { EManagementSystem } from "@Settings/types/administrationTools/utils.ts";

type DealerManagementSystemProviderProps = {
	settings: IAdministrationTools;
	titles: string[];
	isExpanded: boolean;
};
export default function DealerManagementSystemProvider(props: DealerManagementSystemProviderProps) {
	const locationId = useLocationId();

	/******************************************************* QUERIES ********************************************************/
	const { data, isLoading } = useQuery({
		queryKey: ["dmsProviderList", locationId],
		queryFn: () => getProviderList(locationId, EManagementSystem.DMS),
		enabled: and(Boolean(locationId), props.isExpanded),
		staleTime: Infinity,
	});

	/******************************************************* MUTATIONS ********************************************************/
	const { mutate: mutateDMS, isPending } = useMutation({
		mutationKey: ["updateDMSSection", locationId],
		mutationFn: async (administrationToolsInput: Partial<IAdministrationTools>) => {
			const provider = get(administrationToolsInput, "integrations.dmsProvider.provider", "");
			const systemTenant = await getSystemTenant({ locationId: locationId as string, systemType: EManagementSystem.DMS, provider });

			const updatedAdministrationTools = set(
				administrationToolsInput,
				"integrations.dmsProvider.systemTenantId",
				systemTenant?.systemTenantId ?? ""
			);

			return updateAdministrationTools(locationId, updatedAdministrationTools);
		},
	});

	const handleChangeValue = (value: { providerName: string; provider: string }) => {
		const updatedIntegrations = {
			...props.settings.integrations,
			dmsProvider: {
				...props.settings.integrations.dmsProvider,
				provider: value.provider,
				providerName: value.providerName,
			},
		};

		const administrationToolsInput = {
			integrations: updatedIntegrations,
		};
		mutateDMS(administrationToolsInput);
	};

	const handleChangeUserId = (value: string) => {
		const updatedIntegrations = {
			...props.settings.integrations,
			dmsProvider: {
				...props.settings.integrations.dmsProvider,
				userID: value,
			},
		};
		const administrationToolsInput = {
			integrations: updatedIntegrations,
		};
		mutateDMS(administrationToolsInput);
	};

	const isLoadingOrEditing = or(isLoading, isPending);

	return (
		<Stack display={"flex"} direction={"row"} marginTop={1} gap={3}>
			<Stack display={"flex"} direction={"column"} width={218}>
				<Typography className="!font-montserrat !text-[16px] !text-default !font-medium !mb-3">{props.titles[0]}</Typography>
				<SmartSelector
					selectBy={"providerName"}
					showBy={"providerName"}
					options={(data ?? []) as object[]}
					selected={props.settings?.integrations?.dmsProvider}
					inputProps={{
						placeholder: "Select DMS",
					}}
					disabled={isLoadingOrEditing}
					loading={isLoadingOrEditing}
					sx={{ minWidth: "84px" }}
					onChangeAction={(option) => handleChangeValue(option as { provider: string; providerName: string })}
				/>
			</Stack>
			<Stack display={"flex"} direction={"column"} width={218}>
				<Typography className="!font-montserrat !text-[16px] !text-default !font-medium !mb-3">{props.titles[1]}</Typography>
				<SimpleTextField
					editable={or(isLoading, !isPending)}
					disabled={isLoadingOrEditing}
					outlined
					text={props.settings?.integrations?.dmsProvider?.userID}
					placeholder={"User ID"}
					onActionUpdate={(value) => handleChangeUserId(value)}
				/>
			</Stack>
		</Stack>
	);
}
